import {
  IListEmployee,
  IEmployee,
  IEmployeeExpense,
  IEmployeeWalletBenefit,
  IEmployeeWalletRecognitions,
  IBaseEmployee,
} from 'types/income';
import { converters as tableConverters } from 'store/table';
import {
  Expense,
  convertExpenseSource,
  convertExpenseType,
} from 'store/expenses';
import { convertEmbeddedCustomCategories } from 'store/custom-categories';
import { loaders } from './employees.loaders';
import {
  ListEmployee,
  Employee,
  EmployeeJobTitle,
  EmployeeWalletBenefit,
  EmployeeWalletRecognitions,
  BaseEmployee,
} from './employees.types';

const convertBaseEmployee = (employee: IBaseEmployee): BaseEmployee => {
  return {
    id: employee.id,
    firstName: employee.firstName,
    lastName: employee.lastName,
    fullName: [employee.firstName, employee.lastName].filter(Boolean).join(' '),
    email: employee.email ?? '',
    phone: employee.phone ?? '',
    hireDate: employee.employmentDate,
    consents: employee.consents,
  };
};

const convertListEmployee = (employee: IListEmployee): ListEmployee => {
  return {
    ...convertBaseEmployee(employee),
    employmentStatus: employee.employmentStatus,
    birthday: employee.birthDate || null,
    isAppUser: employee.appUser,
    customCategories: convertEmbeddedCustomCategories(
      employee.customCategories ?? [],
    ),
  };
};

const convertEmployee = (employee: IEmployee): Employee => {
  return {
    ...convertBaseEmployee(employee),
    dateOfBirth: employee.dateOfBirth || null,
    parentCompanyId: employee.parentCompanyId,
    capacity: employee.capacity ?? 'fullTime',
    companyId: employee.companyId,
    departmentId: employee.departmentId ?? '',
    teamId: employee.teamId ?? '',
    employeeId: employee.employeeId ?? '',
    externalId: employee.externalId ?? '',
    iban: employee.iban ?? '',
    avatarId: employee.avatarId ?? null,
    customCategories: convertEmbeddedCustomCategories(
      employee.employeeToCategories.map((category) => ({
        id: category.employeeCategoryId,
        name: '',
        valueId: category.employeeCategoryValueId,
        value: '',
      })),
    ),
    employmentDateTZ: employee.employmentDateTZ,
    employmentDuration: employee.employmentDuration,
    employmentTime: employee.employmentTime,
    endDate: employee.endDate,
    endDateTZ: employee.endDateTZ,
    startDate: employee.startDate,
    startDateTZ: employee.startDateTZ,
    endTime: employee.endTime,
    startTime: employee.startTime,
    internalComment: employee.internalComment ?? '',
    jobTitle: employee.jobTitle ?? '',
    language: employee.language,
    permission: employee.permission,
    personalCode: employee.personalCode ?? '',
    status: employee.status,
    temporaryEmployment: employee.temporaryEmployment,
    userId: employee.userId,
    user: employee.user
      ? {
          id: employee.user.id,
          lastActionDate: employee.user.lastActionDate,
        }
      : null,
    cognito: employee.cognito
      ? {
          status: employee.cognito.status,
          user: employee.cognito.user
            ? {
                username: employee.cognito.user.username,
                sub: employee.cognito.user.sub,
                email: employee.cognito.user.email,
                phone: employee.cognito.user.phone,
                userStatus: employee.cognito.user.userStatus,
              }
            : null,
        }
      : null,
  };
};

const convertEmployees = (employees: IListEmployee[]): ListEmployee[] => {
  return employees.map(convertListEmployee);
};

const convertExpense = (expense: IEmployeeExpense): Expense => {
  return {
    id: expense.id,
    createdAt: expense.creationDate,
    date: expense.date,
    description: expense.description,
    employee: null,
    categories: expense.itemCategories,
    amount: expense.expense,
    source: convertExpenseSource(
      // @ts-expect-error empty ids are filtered out
      expense.sourceBenefits?.filter((benefit) => !!benefit.id) ?? [],
      !!expense.recognitionReward,
    ),
    status: expense.status,
    type: convertExpenseType(expense.type),
  };
};

const convertExpenses = (expenses: IEmployeeExpense[]): Expense[] => {
  return expenses.map(convertExpense);
};

const convertEmployeeWalletBenefit = (
  benefit: IEmployeeWalletBenefit,
): EmployeeWalletBenefit => {
  return {
    id: benefit.id,
    name: benefit.name,
    status: benefit.status,
    assignmentStatus: benefit.assignmentStatus,
    investment: benefit.investmentAmount ?? 0,
    expenses: benefit.expenses,
    remaining: benefit.remaining,
  };
};

const convertEmployeeWalletBenefits = (
  benefits: IEmployeeWalletBenefit[],
): EmployeeWalletBenefit[] => {
  return benefits.map(convertEmployeeWalletBenefit);
};

const convertWalletRecognitions = (
  recognitions: IEmployeeWalletRecognitions,
): EmployeeWalletRecognitions => {
  return {
    availableCurrency: recognitions.availableCurrency,
    availablePoints: recognitions.availablePoints,
    spentCurrency: recognitions.spentCurrency,
    spentPoints: recognitions.spentPoints,
    totalCurrency: recognitions.totalCurrency,
    totalPoints: recognitions.totalPoints,
    expiredPoints: recognitions.expiredPoints,
    expiredCurrency: recognitions.expiredCurrency,
  };
};

const convertEmployeeJobTitle = (jobTitle: string): EmployeeJobTitle => {
  return {
    name: jobTitle,
  };
};

const convertEmployeeJobTitles = (jobTitles: string[]): EmployeeJobTitle[] => {
  return jobTitles.map(convertEmployeeJobTitle);
};

export const converters = {
  getEmployees: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getEmployees>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertEmployees(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getEmployee: ({ data }: Awaited<ReturnType<typeof loaders.getEmployee>>) => {
    return convertEmployee(data);
  },
  getExpenses: ({ data }: Awaited<ReturnType<typeof loaders.getExpenses>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertExpenses(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getWalletBenefits: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getWalletBenefits>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertEmployeeWalletBenefits(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getWalletRecognitions: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getWalletRecognitions>>) => {
    return convertWalletRecognitions(data);
  },
  getJobTitles: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getJobTitles>>) => {
    return convertEmployeeJobTitles(data);
  },
  getUniqueJobTitles: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getUniqueJobTitles>>) => {
    return convertEmployeeJobTitles(data);
  },
};
